// ============================================================
// Tracking + validation helpers (dataLayer, UTM, webhook, fbq)
// ============================================================

const PERSONAL_DOMAINS = new Set([
  "gmail.com",
  "yahoo.com",
  "yahoo.in",
  "hotmail.com",
  "outlook.com",
  "rediffmail.com",
  "ymail.com",
]);

export function isValidEmailFormat(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim());
}

export function isPersonalEmail(email) {
  const domain = String(email || "")
    .trim()
    .toLowerCase()
    .split("@")[1];
  if (!domain) return false;
  return PERSONAL_DOMAINS.has(domain);
}

export function digitCount(s) {
  return (String(s || "").match(/\d/g) || []).length;
}

// ---- UTM ---------------------------------------------------
export function getUtmParams() {
  if (typeof window === "undefined") return {};
  const p = new URLSearchParams(window.location.search);
  return {
    source: p.get("utm_source") || "",
    medium: p.get("utm_medium") || "",
    campaign: p.get("utm_campaign") || "",
    content: p.get("utm_content") || "",
    term: p.get("utm_term") || "",
    fbclid: p.get("fbclid") || "",
  };
}

// ---- dataLayer --------------------------------------------
export function pushDataLayer(payload) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

let _pageViewSent = false;

export function initPageView() {
  const utm = getUtmParams();
  if (_pageViewSent) return utm;
  _pageViewSent = true;
  pushDataLayer({
    event: "page_view",
    utm_source: utm.source,
    utm_medium: utm.medium,
    utm_campaign: utm.campaign,
    utm_content: utm.content,
    utm_term: utm.term,
    fbclid: utm.fbclid,
  });
  return utm;
}

// ---- Scroll depth ----------------------------------------
export function initScrollDepthTracking() {
  if (typeof window === "undefined") return () => {};
  const fired = new Set();
  const thresholds = [25, 50, 75, 100];

  const onScroll = () => {
    const doc = document.documentElement;
    const scrolled = window.scrollY || doc.scrollTop || 0;
    const maxScroll = (doc.scrollHeight || 0) - window.innerHeight;
    if (maxScroll <= 0) return;
    const pct = Math.min(100, (scrolled / maxScroll) * 100);
    for (const t of thresholds) {
      if (pct >= t && !fired.has(t)) {
        fired.add(t);
        pushDataLayer({ event: "scroll_depth", depth_percentage: t });
      }
    }
  };

  window.addEventListener("scroll", onScroll, { passive: true });
  // fire once immediately in case of tall short viewport
  onScroll();
  return () => window.removeEventListener("scroll", onScroll);
}

// ---- Meta Pixel ------------------------------------------
export function fireFbqCompleted(score, band) {
  try {
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("trackCustom", "ScoreCardCompleted", { score, band });
    }
  } catch (_e) {
    /* swallow */
  }
}

// ---- Webhook ---------------------------------------------
const WEBHOOK_URL =
  "https://services.leadconnectorhq.com/hooks/2k6r5L1Mje6DsGvxg8Ps/webhook-trigger/8a984347-6c5d-4d93-a098-6dfe8f16df01";

export async function fireWebhook(payload) {
  try {
    await fetch(WEBHOOK_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      // fire-and-forget; short timeout via AbortController not required
    });
  } catch (e) {
    console.warn("Webhook submission failed:", e);
  }
}
